import React, { useContext, useMemo } from 'react';
import PracticePeriodPill from '../components/pill';
import {
  formatIsoTzLess as formatIso,
  getWeekDay,
  WeekYear,
} from '../date.utils';
import { CalendarUhaContext } from './calendarUhaContext';
import {
  FSAktivitetstatustypeDto,
  PraksisaktivitetStatusDto,
  PraksiskalenderAktivitetDto,
  PraksisklasseDto,
} from '../api/generated/studiested-generated-api';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { practicePeriodColorClass } from './calendarModels';
import { UhaUserContext } from '../user/UhaUserStore';
import { BrukerrollerDto } from '../api/generated/praksissted-generated-api';
import translation from '../practiceactivity/calendarCell.uha.i18n';
import { useFormatMessageFor } from '../i18n/translation';

export const findKlasseWithinCellDateRange = (
  klasser: PraksisklasseDto[],
  startYear: number,
  isWeekly: boolean,
  weekYear: WeekYear,
  dayOfWeek: number,
) =>
  klasser.find((klasse) =>
    isWeekly
      ? [1, 2, 3, 4, 5, 6, 7].some((day) =>
          checkCellOnEpoch(klasse, weekYear, day),
        )
      : checkCellOnEpoch(klasse, weekYear, dayOfWeek),
  );

const checkCellOnEpoch = function (
  klasse: PraksisklasseDto,
  weekYear: WeekYear,
  dayOfWeek: number,
) {
  const cellDate = getWeekDay(weekYear, dayOfWeek - 1).getTime();

  const start = klasse.undervisningsenhetPeriode.start;
  const slutt = klasse.undervisningsenhetPeriode.slutt;

  return slutt >= cellDate && start <= cellDate;
};

const useStatusTranslation = (
  status: PraksisaktivitetStatusDto | undefined,
  fsStatus: FSAktivitetstatustypeDto | undefined,
): string => {
  const labels = useFormatMessageFor(translation);
  const fsStatusFeilNotAvtalefeil =
    fsStatus !== FSAktivitetstatustypeDto.IngenFeil &&
    status !== PraksisaktivitetStatusDto.Avtalefeil;

  if (!status || fsStatusFeilNotAvtalefeil) return '';

  switch (status) {
    case PraksisaktivitetStatusDto.ManglerBehov:
      return labels.manglerBehov;
    case PraksisaktivitetStatusDto.DeltMedNoenPraksissteder:
    case PraksisaktivitetStatusDto.KanDeles:
      return labels.kanDeles;
    case PraksisaktivitetStatusDto.DeltMenManglerKapasitet:
      return labels.deltMenManglerKapasitet;
    case PraksisaktivitetStatusDto.DeltOgHarKapasitet:
    case PraksisaktivitetStatusDto.AvsluttetOnskeliste:
      return labels.deltOgHarKapasitet;
    case PraksisaktivitetStatusDto.VenterPaaOnskeliste:
      return labels.venterPaaOnskeliste;
    case PraksisaktivitetStatusDto.StudenterManglerKlasse:
      return labels.studenterManglerKlasse;
    case PraksisaktivitetStatusDto.StudenterManglerPlass:
      return labels.studenterManglerPlass;
    case PraksisaktivitetStatusDto.AlleStudenterFordelt:
      return labels.alleStudenterFordelt;
    case PraksisaktivitetStatusDto.FordelingsresultatDelt:
    case PraksisaktivitetStatusDto.Aktiv:
      return labels.fordelingsresultatDelt;
    case PraksisaktivitetStatusDto.Gjennomfoert:
      return labels.gjennomfoert;
    case PraksisaktivitetStatusDto.Avtalefeil:
      return labels.avtalefeil;
  }
};

const useFsStatusError = (
  fsStatus: FSAktivitetstatustypeDto | undefined,
): string => {
  const labels = useFormatMessageFor(translation);

  switch (fsStatus) {
    case FSAktivitetstatustypeDto.FeilVedPublisering:
      return labels.fsFeilPublisering;
    case FSAktivitetstatustypeDto.UkjentFeil:
      return labels.fsFeilUkjent;
    case FSAktivitetstatustypeDto.IngenFeil:
    case undefined:
      return '';
  }
};

const useStatusLabel = (
  aktivitet: PraksiskalenderAktivitetDto | null,
): string => {
  const status = aktivitet?.status;
  const fsStatus = aktivitet?.fsStatus;
  const statusLabel = useStatusTranslation(status, fsStatus);
  const fsStatusError = useFsStatusError(fsStatus);

  return fsStatusError ? `${fsStatusError} ${statusLabel}` : statusLabel;
};

interface CellProps {
  weekYear: WeekYear;
  dayOfWeek: number;
  klasser: PraksisklasseDto[];
  onClick?: Function;
  periodsReloaded: Function;
  mouseIn: Function;
  mouseOut: Function;
  aktivitet: PraksiskalenderAktivitetDto | null;
  isWeekly: boolean;
  startYear: number;
  lastRenderedDate: string;
}

const CalendarCell: React.FC<React.PropsWithChildren<CellProps>> = ({
  weekYear,
  dayOfWeek,
  klasser,
  mouseIn,
  mouseOut,
  aktivitet,
  isWeekly,
  onClick = () => {},
  periodsReloaded = () => {},
  startYear,
  lastRenderedDate,
}) => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { earliestVisibleIsoDate } = useContext(CalendarUhaContext);
  const { uhaUser: bruker } = useContext(UhaUserContext);

  const onPillClick = (pillHtmlId: string) => {
    if (onClick) {
      const activityId = pillHtmlId.replace('pill-', '');

      onClick(activityId);
    }
  };

  const statusLabel = useStatusLabel(aktivitet);

  const addNewActivitiy = () => {
    let weekDay = dayOfWeek;
    const currentKlasse = klasser.find((klasse) =>
      isWeekly
        ? [1, 2, 3, 4, 5, 6, 7].some((day) => {
            weekDay = day;
            return checkCellOnEpoch(klasse, weekYear, day);
          })
        : checkCellOnEpoch(klasse, weekYear, dayOfWeek),
    );

    const selectedDate = formatIso(getWeekDay(weekYear, weekDay - 1));

    if (currentKlasse) {
      praksiskalenderApi
        .praksisaktivitetPost({
          nyPraksisaktivitetDto: {
            klasseId: currentKlasse.id,
            startDato: selectedDate,
            sluttDato: selectedDate,
          },
        })
        .then((aktivitetsId) => {
          onClick(aktivitetsId, true);
          periodsReloaded(true);
        });
    }
  };

  const locked = useMemo(
    () =>
      findKlasseWithinCellDateRange(
        klasser,
        startYear,
        isWeekly,
        weekYear,
        dayOfWeek,
      ) === undefined,
    [klasser, startYear, isWeekly, weekYear, dayOfWeek],
  );

  const renderAddActivity = useMemo(
    () =>
      !aktivitet &&
      bruker &&
      bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet &&
      !locked,
    [bruker, locked, aktivitet],
  );

  const pillColor = practicePeriodColorClass(aktivitet);

  const hasAktivitetStyle = aktivitet && 'pill-wrapper';
  const lockedStyle = locked ? 'locked' : 'open';

  return (
    <td
      onMouseEnter={() => mouseIn(weekYear)}
      onMouseLeave={() => mouseOut(null)}
      className={hasAktivitetStyle || lockedStyle}
      data-week={weekYear.week}
    >
      {aktivitet && (
        <PracticePeriodPill
          tooltipText={statusLabel}
          colorPatternClass={pillColor}
          practicePeriod={aktivitet}
          onClick={onPillClick}
          pxPerDayScale={isWeekly ? 34 / 7.0 : 21}
          extraClasses={isWeekly ? ['weekly'] : ['daily']}
          isWeekly={isWeekly}
          earliestVisibleIsoDate={earliestVisibleIsoDate}
          latestVisibleIsoDate={lastRenderedDate}
          warningIcon={
            aktivitet.status === PraksisaktivitetStatusDto.Avtalefeil ||
            aktivitet.fsStatus !== FSAktivitetstatustypeDto.IngenFeil
          }
        />
      )}
      {renderAddActivity && (
        <div className="add" onClick={addNewActivitiy}>
          +
        </div>
      )}
    </td>
  );
};

export default CalendarCell;
